import { MutableRefObject, useRef } from 'react';

/**
 * Returns a ref whose `.current` is always the value from the most recent
 * render.
 *
 * Use it when an effect registers a long-lived listener (a cache
 * subscription, event listener, timer) that calls a function passed in from
 * outside. The listener runs many times after the effect that created it,
 * so it has to call the function the component rendered most recently.
 *
 * Calling the function directly inside the listener keeps the version from
 * the render where the effect last ran. If that function reads props or
 * state, every later call sees those old values (a stale closure). Adding
 * the function to the effect's deps fixes that, but callers usually pass an
 * inline arrow with a new identity on every render. Then the listener is
 * torn down and re-registered on every render.
 *
 * Reading through this ref keeps one listener registered for the effect's
 * lifetime and still calls the latest function. The ref's identity never
 * changes, so listing it in deps doesn't re-run the effect.
 *
 * Read `.current` only inside callbacks. During render it holds the same
 * value you passed in.
 *
 * @example
 * const latestOnChange = useLatest(onChange);
 * useEffect(
 *   () => store.subscribe((value) => latestOnChange.current(value)),
 *   [store, latestOnChange]
 * );
 */
export function useLatest<Type>(value: Type): MutableRefObject<Type> {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}
